"use client"

import { useEffect, useRef } from "react"
import { useQuery } from "@tanstack/react-query"
import { useTheme } from "next-themes"

import { useAuth } from "@/hooks/use-auth"
import { themeService } from "@/services/themes"
import {
    applyTheme,
    loadSelectedSlug,
    normalizeTheme,
    STORAGE_KEY,
} from "@/lib/custom-theme"

/**
 * Keeps the custom-theme slots in sync with the server, and reports which
 * theme this user is on so the Studio can show adoption.
 *
 * Two layers on purpose:
 *  - localStorage is the *cache*, read by the boot script in <head> so the
 *    right colours paint on the very first frame with no network wait.
 *  - the server is the *source of truth*, so a theme edited elsewhere (or a
 *    device that has never seen this palette) still lands correctly.
 */
export function CustomThemeSync() {
    const { isAuthenticated } = useAuth()
    const { theme } = useTheme()
    const lastReported = useRef<string | null>(null)

    const { data: themes } = useQuery({
        queryKey: ["customThemes"],
        queryFn: themeService.list,
        enabled: isAuthenticated,
        retry: false,
        staleTime: 1000 * 60 * 5,
    })

    // Refresh the slot palettes from the server copy of the selected theme.
    useEffect(() => {
        if (!themes?.length) return
        const slug = loadSelectedSlug()
        if (!slug) return

        const selected = themes.find((t) => t.slug === slug)
        if (!selected) return

        const normalized = normalizeTheme({ light: selected.light, dark: selected.dark })
        applyTheme(normalized)
        try {
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized))
        } catch {
            // non-fatal
        }
    }, [themes])

    // Report the active theme. Custom themes report as "custom:<slug>" so the
    // stats distinguish between the individual palettes, not just "custom".
    useEffect(() => {
        if (!isAuthenticated || !theme) return

        let reported = theme
        if (theme === "custom" || theme === "custom-dark") {
            const slug = loadSelectedSlug()
            if (!slug) return
            reported = `custom:${slug}`
        }

        if (lastReported.current === reported) return
        lastReported.current = reported

        themeService.reportUsage(reported).catch(() => {
            // Usage tracking is best-effort; never surface it to the user.
            lastReported.current = null
        })
    }, [theme, isAuthenticated, themes])

    return null
}
